/**
 * Pure-function helpers for the bulk-mode modal morph (Multi-Add
 * Phase 1).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). The modal-
 * morph contract:
 *
 *   - The modal switches from a two-column dual-pane (selector +
 *     preview) to a full-width single-column selector when `bulkMode`
 *     is set by the multi-select machinery.
 *   - The header title pluralizes ("Add a video source" → "Add video
 *     sources").
 *   - The wizard stepper is hidden in bulk mode (one-shot commit, no
 *     subsequent panels).
 *   - The morph reverts to single-add when bulkMode collapses. ENG-4485:
 *     `bulkMode` is DERIVED — true iff the selection is non-empty — so the
 *     morph fires on the FIRST tick and reverts on the LAST untick. This is
 *     NOT #1228's threshold=2: a lone checked camera is bulk, never a
 *     single-add bridge. Feature: bulk-add-sources-feature.
 *
 * Per `think-in-systems` (ai.knowledge/global): extracting the
 * conditional into pure functions lets `<Scaffold>`, `<Content>`, and
 * `<Footer>` consume the same morph contract without duplicating the
 * `bulkMode === true ? … : …` ternary across three components — and
 * lets the contract be tested in isolation (no React render required,
 * matching the pattern set by W3's `chip-state.ts`).
 *
 * Per `tests-require-user-story-comment` rule: the sibling
 * `morph-state.test.ts` carries one user-story comment per branch.
 */

/** Singular title for the single-add modal shell. */
export const TITLE_SINGLE = 'Add a video source';

/** Plural title rendered when bulk mode is active. */
export const TITLE_BULK = 'Add video sources';

/** Plural section heading rendered in bulk mode's full-width column. */
export const HEADING_BULK = 'Select video sources';

/** Singular section heading rendered in the single-add left column. */
export const HEADING_SINGLE = 'Select a video source';

/**
 * Resolve the modal header title from the bulk-mode signal.
 *
 * @param bulkMode - True iff the selection is non-empty (ENG-4485: derived
 *   from `multiSelection.length > 0`). At 0 selected it is false
 *   (single-add); the first tick flips it true. Feature:
 *   bulk-add-sources-feature.
 */
export const resolveModalTitle = (bulkMode: boolean): string =>
  bulkMode ? TITLE_BULK : TITLE_SINGLE;

/**
 * Whether the wizard stepper should render. The single-add flow walks
 * Source → Video → Manufacturer → Control; bulk mode commits directly
 * from the source panel and skips those steps entirely. Stepper hidden
 * in bulk mode.
 */
export const shouldShowStepper = (bulkMode: boolean): boolean => !bulkMode;

/**
 * Whether the right-hand preview/bridge column should render in the
 * source panel. Bulk mode bypasses the per-selection preview, so the
 * preview column would have nothing to display.
 */
export const shouldShowPreviewColumn = (bulkMode: boolean): boolean => !bulkMode;

/**
 * Resolve the section heading inside the source panel's selector
 * column.
 */
export const resolveSelectorHeading = (bulkMode: boolean): string =>
  bulkMode ? HEADING_BULK : HEADING_SINGLE;
